<template>
  <div class="page-container">
    <div class="toolbar">
      <div class="actions">
        <el-button @click="openBatchDialog('add')">批量加人</el-button>
        <el-button @click="openBatchDialog('clear')">批量清空</el-button>
      </div>
      <div class="filters">
        <el-input v-model="query.keyword" placeholder="粮库名称/负责人" clearable style="width: 220px" @keyup.enter="loadData" />
        <el-button type="primary" @click="loadData">查询</el-button>
        <el-button @click="resetQuery">重置</el-button>
      </div>
    </div>

    <div class="table-wrap">
      <el-table :data="filteredStores" border stripe height="calc(100vh - 160px)" v-loading="loading" row-key="id"
                @selection-change="onSelectionChange">
        <el-table-column type="selection" width="44" />
        <el-table-column prop="store_name" label="粮库" width="180" fixed="left" show-overflow-tooltip />
        <el-table-column prop="manager" label="负责人" width="110" show-overflow-tooltip />

        <el-table-column v-for="r in roleTypes" :key="r.value" :label="r.label" min-width="180" show-overflow-tooltip>
          <template #default="{ row }">
            <div class="role-cell">
              <el-tag v-for="u in (roleDetails[row.id]?.[r.value] || [])" :key="u.id" size="small" class="role-tag"
                      :closable="false">
                <span class="tag-text">{{ u.real_name || u.username }}</span>
                <span class="tag-close" @click="removeUser(row.id, r.value, u.id)" title="移除">×</span>
              </el-tag>
              <el-button link type="primary" size="small"
                         @click="openPicker(row.id, r.value, r.label, roleDetails[row.id]?.[r.value] || [])">
                {{ (roleDetails[row.id]?.[r.value] || []).length ? '编辑' : '+ 添加' }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 单行编辑对话框 -->
    <el-dialog v-model="pickerVisible" :title="'编辑 ' + pickerLabel + '（' + pickerStoreName + '）'" width="760px">
      <el-transfer
        v-model="pickerSelected"
        :data="transferData"
        :titles="['可选用户', '已分配']"
        filterable
        filter-placeholder="搜索姓名/账号"
      />
      <template #footer>
        <el-button @click="pickerVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmPicker">保存</el-button>
      </template>
    </el-dialog>

    <!-- 批量操作对话框 -->
    <el-dialog v-model="batchVisible" :title="batchTitle" width="520px">
      <el-form label-width="100px">
        <el-form-item label="已选粮库">
          <span>{{ selectedStoreNames }}</span>
        </el-form-item>
        <el-form-item label="岗位">
          <el-select v-model="batchRole" placeholder="选择岗位" style="width: 100%">
            <el-option v-for="r in roleTypes" :key="r.value" :label="r.label" :value="r.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="用户" v-if="batchMode === 'add'">
          <el-select v-model="batchUsers" multiple filterable placeholder="选择要分配的用户" style="width: 100%">
            <el-option v-for="u in users" :key="u.id"
                       :label="(u.real_name || u.username) + ' (' + u.username + ')'"
                       :value="u.id" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="batchVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmBatch">执行</el-button>
      </template>
    </el-dialog>
  </div>

  <!-- 自定义 toast（完全避开 Element Plus createMessage bug） -->
  <div v-if="toastShow" class="store-role-toast" :class="'toast-' + toastType">
    {{ toastMsg }}
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import request from '../utils/request'

// ---- 自定义 toast（避开 Element Plus 的 ElMessage createMessage bug）----
const toastMsg = ref('')
const toastType = ref('success')
const toastShow = ref(false)
function toast(msg, type = 'success') {
  toastMsg.value = msg
  toastType.value = type
  toastShow.value = true
  clearTimeout(toast._t)
  toast._t = setTimeout(() => { toastShow.value = false }, 1800)
}

const roleTypes = ref([
  { value: 'contract_admin',     label: '合同管理员' },
  { value: 'workorder_receiver', label: '工单接收人' },
  { value: 'workorder_handler',  label: '工单处理人' },
  { value: 'patrol_member',      label: '巡检员' },
  { value: 'alarm_viewer',       label: '告警/监控查看员' }
])

const stores = ref([])
const users = ref([])
const roleDetails = ref({})
const loading = ref(false)
const selected = ref([])

const query = ref({ keyword: '', roleType: '', hasPeople: '' })

// 单行编辑对话框状态
const pickerVisible = ref(false)
const pickerStoreId = ref(null)
const pickerRoleType = ref('')
const pickerLabel = ref('')
const pickerStoreName = ref('')
const pickerSelected = ref([])

const transferData = computed(() =>
  users.value.map(u => ({
    key: u.id,
    label: (u.real_name || u.username) + ' (' + u.username + ')'
  }))
)

// 批量对话框状态
const batchVisible = ref(false)
const batchMode = ref('add')
const batchRole = ref('')
const batchUsers = ref([])

// ---- 过滤 ----
const filteredStores = computed(() => {
  const kw = query.value.keyword?.toLowerCase()
  return stores.value.filter(s => {
    if (kw) {
      const hit = (s.store_name || '').toLowerCase().includes(kw) || (s.manager || '').toLowerCase().includes(kw)
      if (!hit) return false
    }
    return true
  })
})

const selectedStoreNames = computed(() =>
  selected.value.map(id => stores.value.find(s => s.id === id)?.store_name || '#' + id).join('、')
)

const batchTitle = computed(() => {
  const r = roleTypes.value.find(x => x.value === batchRole.value)
  const rn = r ? r.label : '所选岗位'
  return batchMode.value === 'clear' ? `批量清空：${rn}` : `批量加人：${rn}`
})

// ---- 加载 ----
async function loadData() {
  loading.value = true
  try {
    // 并行 3 次请求（之前是 2 + N 次循环！）
    const [sRes, uRes, allRes] = await Promise.all([
      request.get('/store-role/stores'),
      request.get('/store-role/users'),
      request.get('/store-role/all')
    ])
    stores.value = sRes.data || []
    users.value = uRes.data || []
    const allDetails = allRes.data || []

    const map = {}
    stores.value.forEach(s => { map[s.id] = {} })
    for (const d of allDetails) {
      if (!map[d.grain_store_id]) map[d.grain_store_id] = {}
      if (!map[d.grain_store_id][d.role_type]) map[d.grain_store_id][d.role_type] = []
      map[d.grain_store_id][d.role_type].push({
        id: d.user_id, username: d.username, real_name: d.real_name, phone: d.phone
      })
    }
    roleDetails.value = map
  } finally {
    loading.value = false
  }
}

function resetQuery() {
  query.value = { keyword: '', roleType: '', hasPeople: '' }
  loadData()
}

function onSelectionChange(rows) { selected.value = rows.map(r => r.id) }

// ---- 单行编辑 ----
function openPicker(storeId, roleType, label, currentUsers) {
  pickerStoreId.value = storeId
  pickerRoleType.value = roleType
  pickerLabel.value = label
  pickerStoreName.value = stores.value.find(s => s.id === storeId)?.store_name || ''
  pickerSelected.value = currentUsers.map(u => u.id)
  pickerVisible.value = true
}

async function confirmPicker() {
  await request.post('/store-role/set', {
    storeId: pickerStoreId.value,
    roleType: pickerRoleType.value,
    userIds: pickerSelected.value
  })
  pickerVisible.value = false
  toast('已保存')
  loadDataSilent()
}

function removeUser(storeId, roleType, userId) {
  const list = roleDetails.value[storeId]?.[roleType]
  if (!list) return
  const idx = list.findIndex(u => u.id === userId)
  if (idx < 0) return
  const removed = list[idx]
  list.splice(idx, 1)

  request.post('/store-role/set', {
    storeId, roleType,
    userIds: list.map(u => u.id)
  }).then(() => {
    toast('已移除')
  }).catch(e => {
    list.splice(idx, 0, removed)
    toast(e?.message || '操作失败', 'error')
  })
}

/** 静默刷新 — 不设 loading=true → 无 v-loading 遮罩 */
async function loadDataSilent() {
  try {
    const [sRes, uRes, allRes] = await Promise.all([
      request.get('/store-role/stores'),
      request.get('/store-role/users'),
      request.get('/store-role/all')
    ])
    stores.value = sRes.data || []
    users.value = uRes.data || []
    const map = {}
    stores.value.forEach(s => { map[s.id] = {} })
    for (const d of (allRes.data || [])) {
      if (!map[d.grain_store_id]) map[d.grain_store_id] = {}
      if (!map[d.grain_store_id][d.role_type]) map[d.grain_store_id][d.role_type] = []
      map[d.grain_store_id][d.role_type].push({
        id: d.user_id, username: d.username, real_name: d.real_name, phone: d.phone
      })
    }
    roleDetails.value = map
  } catch (e) { /* 静默失败不弹 toast，避免乱 */ }
}

// ---- 批量 ----
function openBatchDialog(mode) {
  if (!selected.value.length) { toast('请先勾选粮库', 'warning'); return }
  batchMode.value = mode
  batchRole.value = ''
  batchUsers.value = []
  batchVisible.value = true
}

async function confirmBatch() {
  if (!batchRole.value) { toast('请选择岗位', 'warning'); return }
  try {
    let msg = ''
    if (batchMode.value === 'add') {
      const r = await request.post('/store-role/batch-add', {
        storeIds: selected.value, roleType: batchRole.value, userIds: batchUsers.value
      })
      msg = `已新增 ${r?.data?.added || 0} 条配置`
    } else {
      const r = await request.post('/store-role/batch-clear', {
        storeIds: selected.value, roleType: batchRole.value
      })
      msg = `已清空 ${r?.data?.cleared || 0} 条配置`
    }
    batchVisible.value = false
    toast(msg)
    loadData()
  } catch (e) {
    toast(e?.message || '操作失败', 'error')
  }
}

onMounted(loadData)
</script>

<style scoped>
.toolbar { display: flex; justify-content: space-between; margin-bottom: 12px; gap: 12px; flex-wrap: wrap; }
.toolbar .actions { display: flex; gap: 8px; }
.toolbar .filters { display: flex; gap: 8px; flex-wrap: wrap; }

/* 表格包裹层 — 允许横向滚动（多列场景） */
.table-wrap {
  width: 100%;
  overflow-x: auto;
}
.table-wrap :deep(.el-table) {
  width: 100%;
  min-width: 1200px; /* 8 列总宽最低值，保证不挤压表头 */
}

.role-cell { display: flex; flex-wrap: wrap; gap: 4px; align-items: center; }
.role-tag { margin: 0; cursor: default; }
.role-tag .tag-text { vertical-align: middle; }
.role-tag .tag-close {
  display: inline-block;
  margin-left: 4px;
  width: 16px; height: 16px;
  line-height: 14px; text-align: center;
  border-radius: 50%;
  font-size: 12px; color: #909399;
  cursor: pointer;
  vertical-align: middle;
  transition: all 0.15s;
}
.role-tag .tag-close:hover { background: rgba(0,0,0,0.1); color: #f56c6c; }

.store-role-toast {
  position: fixed; top: 40px; left: 50%; transform: translateX(-50%);
  padding: 10px 20px; border-radius: 4px; font-size: 14px;
  z-index: 9999; box-shadow: 0 2px 12px rgba(0,0,0,0.15);
  animation: toast-in 0.2s ease;
}
@keyframes toast-in {
  from { opacity: 0; transform: translate(-50%, -10px); }
  to { opacity: 1; transform: translate(-50%, 0); }
}
.toast-success { background: #f0f9eb; color: #67c23a; border: 1px solid #e1f3d8; }
.toast-error   { background: #fef0f0; color: #f56c6c; border: 1px solid #fde2e2; }
.toast-warning { background: #fdf6ec; color: #e6a23c; border: 1px solid #faecd8; }
</style>
